import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { UserUploadUrl } from 'libs/end-to-end/src/types/users/user-upload-url';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { doHttpRequestRaw } from 'libs/end-to-end/src/http/do-http-request';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';

export type UserUploadHeadlessErrorFn = (error: string) => void;
export type UserUploadHeadlessCompleteFn = (userUploadId: string) => void;

export interface UserUploadHeadlessHook {
  upload: (
    blob: Blob,
    completeFn?: UserUploadHeadlessCompleteFn,
    errorFn?: UserUploadHeadlessErrorFn,
  ) => void;
  canUpload: boolean;
  userUploadId: string | null;
  presignedUrl: string | null;
}

export const useUserUploadHeadless = ({
  userId,
}: {
  userId: string | null;
}): UserUploadHeadlessHook => {
  const { myUserStatus } = useUser();

  const [userUploadId, setUserUploadId] = useState<string | null>(null);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);

  const getUrlFn = useCallback(() => {
    return userId === null
      ? null
      : `${getApiBaseUrl()}/users/${userId}/uploads`;
  }, [userId]);

  const urlWritePattern = useSimpleWritePattern({
    getUrlFn,
    writeSchema: EmptyObject,
    method: 'POST',
    canWrite: true,
    responseSchema: UserUploadUrl,
  });

  // Get the presigned URL any time we are in need.
  useAsyncEffect(
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || myUserStatus !== 'logged_in') {
        return;
      }

      if (
        urlWritePattern.status === 'done' &&
        !urlWritePattern.saved &&
        (!presignedUrl || !userUploadId)
      ) {
        urlWritePattern.saveEntity({});
      }
    },
    [myUserStatus, presignedUrl, urlWritePattern, userUploadId],
  );

  useEffect(() => {
    if (
      urlWritePattern.status === 'done' &&
      urlWritePattern.saved &&
      urlWritePattern.entity
    ) {
      setUserUploadId(urlWritePattern.entity.uploadId);
      setPresignedUrl(urlWritePattern.entity.url);
    }
  }, [urlWritePattern]);

  const upload = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      blob: Blob,
      completeFn?: UserUploadHeadlessCompleteFn,
      errorFn?: UserUploadHeadlessErrorFn,
    ) => {
      if (!isAlive() || !presignedUrl || !userUploadId || uploadInProgress) {
        return;
      }

      setUploadInProgress(true);

      try {
        const doRequestResult = await doHttpRequestRaw({
          url: presignedUrl,
          logger: uiAppLogger,
          method: 'PUT',
          headers: {
            'Content-Type': blob.type,
          },
          data: await blob.arrayBuffer(),
        });

        if (
          !isAlive() ||
          !doRequestResult ||
          doRequestResult.resultType === 'err'
        ) {
          if (errorFn) {
            errorFn('There was an error uploading the file');
          }

          return;
        }

        if (completeFn) {
          setPresignedUrl(null);
          setUserUploadId(null);
          completeFn(userUploadId);
        }
      } finally {
        setUploadInProgress(false);
      }
    },
    [presignedUrl, userUploadId, uploadInProgress],
  );

  return useMemo(() => {
    return {
      upload,
      canUpload: !uploadInProgress,
      userUploadId,
      presignedUrl,
    };
  }, [upload, uploadInProgress, userUploadId, presignedUrl]);
};
